import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UtilService } from 'src/app/services/util.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RepoManagementService } from '../repo-management/repo-management.service';
interface RefCreationWrapper {
  refsName: string;
  gitRefsName: string;
  sourceBranchAppSid: string;
  refType: 'BRANCH' | 'TAG';
  sourceRefType: 'BRANCH' | 'TAG';
}

interface GenerationStatusResponse {
  appId?: string;
  orgId?: string;
  generationStatus?: string;
  referenceFrameworkMaintenanceStatus?: string;
  appCopyMaintenanceStatus?: string;
  branchMaintenanceStatus?: string;
}

@Component({
  selector: 'vb-create-branch-tag-popup',
  standalone: true,
  imports: [DialogModule, ReactiveFormsModule, TranslateModule, ButtonModule, TooltipModule, CommonModule],
  templateUrl: './create-branch-tag-popup.component.html',
  styleUrl: './create-branch-tag-popup.component.scss'
})
export class CreateBranchTagPopupComponent implements OnChanges, OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly utilService = inject(UtilService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly httpBaseService = inject(HttpBaseService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly repoService = inject(RepoManagementService);
  branchForm: FormGroup;
  popupVisible = false;
  selectedSourceBranchName = '';
  @Input() applicationRefsList: string[] = [];
  @Input() gitRepositoryRefsList: string[] = [];
  @Input() selectedSourceBranch: any = '';
  @Input() refType: 'BRANCH' | 'TAG' = 'BRANCH';
  sourceBranchAppSid = '';
  sourceBranchOrgId = '';
  sourceBranchVersion = '';
  sourceRefType: 'BRANCH' | 'TAG' = 'BRANCH';
  @Output() readonly reloadRepoPopup = new EventEmitter<void>();
  hasUserEditedGitRefName = false;
  isSpinnerVisible = false;
  spinnerMessage = '';
  @ViewChild('refNameInput') refNameInput: ElementRef;
  messageFromResponse = '';
  targetUrlForNavigation = '';
  showSuccessDialog = false;
  showEmptyFieldError = false;
  showUnpublishedWarningMessage = false;

  config = {
    BRANCH: {
      header: 'CREATE_A_BRANCH',
      nameFieldLabel: 'BRANCH_NAME',
      gitNameFieldLabel: 'GIT_BRANCH_NAME',
      inProgressSpinnerHeader: 'CREATING_A_BRANCH',
      refCreationStatus: 'BRANCH_CREATION_STATUS',
      refNameDuplicateError: 'BRANCH_NAME_ALREADY_EXISTS',
      gitRefNameDuplicateError: 'GIT_BRANCH_NAME_ALREADY_EXISTS',
      successMessage: 'BRANCH_CREATED_SUCCESSFULLY',
      failureMessage: 'BRANCH_CREATION_FAILED_MESSAGE',
      creatingBranchSpinnerMessage: 'CREATING_BRANCH_SPINNER_MESSAGE',
      concurrentLimitExceededMsg: 'BRANCH_LIMIT_EXCEEDED'
    },
    TAG: {
      header: 'CREATE_A_TAG',
      nameFieldLabel: 'TAG_NAME',
      gitNameFieldLabel: 'GIT_TAG_NAME',
      inProgressSpinnerHeader: 'CREATING_A_TAG',
      refCreationStatus: 'TAG_CREATION_STATUS',
      refNameDuplicateError: 'TAG_NAME_ALREADY_EXISTS',
      gitRefNameDuplicateError: 'GIT_TAG_NAME_ALREADY_EXISTS',
      successMessage: 'TAG_CREATED_SUCCESSFULLY',
      failureMessage: 'TAG_CREATION_FAILED_MESSAGE',
      creatingBranchSpinnerMessage: 'CREATING_TAG_SPINNER_MESSAGE',
      concurrentLimitExceededMsg: 'TAG_LIMIT_EXCEEDED'
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedSourceBranch'] && this.branchForm) {
      this.branchForm.get('sourceBranch')?.setValue(this.selectedSourceBranch?.refsName);
      this.selectedSourceBranchName = this.selectedSourceBranch?.refsName || '';
      this.sourceBranchAppSid = this.selectedSourceBranch?.sid;
      this.sourceBranchOrgId = this.selectedSourceBranch?.orgId || '';
      this.sourceBranchVersion = this.selectedSourceBranch?.rappitVersion || '';
      this.sourceRefType = this.selectedSourceBranch?.refType || '';
      this.showUnpublishedWarning();
    }
  }

  showUnpublishedWarning() {
    if (!this.sourceBranchAppSid) {
      this.showUnpublishedWarningMessage = false;
      return;
    }

    this.repoService.checkForUnpublishedChanges(this.sourceBranchAppSid).subscribe({
      next: (hasUnpublishedChanges: unknown) => {
        this.showUnpublishedWarningMessage = Boolean(hasUnpublishedChanges);
      },
      error: () => {
        this.showUnpublishedWarningMessage = false;
      }
    });
  }

  ngOnInit() {
    this.utilService.showBranchPopup$.subscribe(() => {
      this.popupVisible = true;
    });
    this.branchForm = this.formBuilder.group({
      sourceBranch: [{ value: this.selectedSourceBranchName, disabled: true }],
      refName: ['', [this.utilService.gitNameValidator()]],
      gitRefName: ['', [this.utilService.gitNameValidator()]]
    });

    // to automatically set git ref name based on ref name
    this.branchForm.get('refName')?.valueChanges.subscribe((refName: string) => {
      if (!this.hasUserEditedGitRefName) {
        if (refName) {
          const gitRef = refName.trim();
          this.branchForm.get('gitRefName')?.setValue(gitRef, { emitEvent: false });
        } else {
          this.branchForm.get('gitRefName')?.setValue('', { emitEvent: false });
        }
      }
      // Mark as dirty so validation errors show for auto-filled value
      if (this.branchForm.get('refName')?.dirty) {
        this.branchForm.get('gitRefName')?.markAsDirty();
      }
    });

    // to track if user has edited git branch name manually
    this.branchForm.get('gitRefName')?.valueChanges.subscribe(() => {
      this.hasUserEditedGitRefName = true;
    });
  }

  onSave() {
    const { refName, gitRefName } = this.branchForm.getRawValue();
    const appId = this.appGlobalService.get('appId');
    const branchControl = this.branchForm.get('refName');
    const gitBranchControl = this.branchForm.get('gitRefName');
    this.showEmptyFieldError = true;
    this.branchForm.markAllAsTouched();
   
    // Duplicate check for refName (case-insensitive)
    if (this.applicationRefsList.some((ref) => ref.toLowerCase() === refName.toLowerCase())) {
      branchControl?.setErrors({
        gitNameError: this.translate.instant(this.config[this.refType]?.refNameDuplicateError)
      });
    }

    // Duplicate check for gitRefName (case-insensitive)
    if (this.gitRepositoryRefsList.some((ref) => ref.toLowerCase() === gitRefName.toLowerCase())) {
      gitBranchControl?.setErrors({
        gitNameError: this.translate.instant(this.config[this.refType]?.gitRefNameDuplicateError)
      });
    }
    
    if(this.sourceRefType === 'TAG' && this.refType === 'TAG'){
           this.utilService.showMessage('error', this.translate.instant('CANNOT_CREATE_TAG_FROM_A_TAG'));
           return;
    }
    if (this.branchForm.valid) {
      const payload: RefCreationWrapper = {
        refsName: refName,
        gitRefsName: gitRefName,
        sourceBranchAppSid: this.sourceBranchAppSid,
        refType: this.refType,
        sourceRefType: this.sourceRefType
      };
     

      this.checkGenerationStatus().then((generationData) => {
        //if generation is in progress for the source branch, do not proceed with branch/tag creation and show error message.
        let canProceed: boolean = true;

        const generationStatus: string = generationData?.generationStatus || '';
        const appCopyMaintenanceStatus: string = generationData?.appCopyMaintenanceStatus || '';
        const referenceMaintenanceStatus: string = generationData?.referenceFrameworkMaintenanceStatus || '';

        canProceed =
          this.showMessageBasedOnGenerationStatus(generationStatus) &&
          this.showMessageBasedOnAppCopyMaintenanceStatus(appCopyMaintenanceStatus) &&
          this.showMessageBasedOnReferenceMaintenanceStatus(referenceMaintenanceStatus);

        if (!canProceed) {
          this.isSpinnerVisible = false;
          this.popupVisible = true;
          return;
        }

         this.popupVisible = false;
         if (!appId || appId === 'ALL') {
        //show spinner when called from repository management
        this.isSpinnerVisible = true;
        this.spinnerMessage = this.translate.instant(this.config[this.refType]?.creatingBranchSpinnerMessage, {
          refName: refName
        });
      }

        this.httpBaseService.post({ ...ApiConstants.createBranchOrTag, showErrorMsg: false }, payload).subscribe({
          next: (res: any) => {
            //branch created successfully
            this.isSpinnerVisible = false;
            this.reloadRepoPopup.emit();
            this.popupVisible = false;
            this.resetForm();
            this.showMessageBasedOnStatusCode(res, 'SUCCESS');
          },
          error: (errorResponse) => {
            this.isSpinnerVisible = false;
            this.popupVisible = false;
            this.resetForm();
            this.showMessageBasedOnStatusCode(errorResponse, 'ERROR');
          }
        });
        this.hasUserEditedGitRefName = false;
      }).catch((error) => {
        console.error('Error checking generation status:', error);
        this.isSpinnerVisible = false;
        this.popupVisible = true;
      });
    }
  }

  checkGenerationStatus(): Promise<GenerationStatusResponse> {
    return new Promise((resolve) => {
      const apiConstants = ApiConstants.getAppGenStatus;
      this.httpBaseService[apiConstants.method](apiConstants, {
        appId: this.sourceBranchAppSid
      }).subscribe({
        next: (genData: any) => {
          resolve(genData);
        },
        error: (error: any) => {
          console.log(error);
          resolve({});
        }
      });
    });
  }

  // Helper to check if error should be shown for a field
  shouldShowError(fieldName: string): boolean {
    const control = this.branchForm.get(fieldName);
    const error = control?.errors?.gitNameError;
    if (!error) return false;
    
    const isEmptyError = error.includes('empty');
    return (control.dirty && !isEmptyError) || (control.touched && this.showEmptyFieldError && isEmptyError);
  }

  //when user clicks on cancel button
  onReset() {
    this.popupVisible = false;
    this.resetForm();
  }

  // Reset form while preserving sourceBranch value
  private resetForm() {
    this.branchForm.reset({
      sourceBranch: this.selectedSourceBranchName,
      refName: '',
      gitRefName: ''
    });
    this.hasUserEditedGitRefName = false;
    this.showEmptyFieldError = false;
  }

  //Automatically focus branch name input when dialog opens
  onDialogShow() {
    this.showUnpublishedWarning();
    setTimeout(() => {
      this.refNameInput?.nativeElement?.focus();
    }, 100);
  }

  private readonly errorCodeTranslationMap: Record<string, string> = {
    'PROJECT_INITIALISATION_IN_PROGRESS': 'PROJECT_INITIALISATION_IN_PROGRESS_MESSAGE',
    'PROJECT_INITIALIZATION_IN_PROGRESS': 'PROJECT_INITIALISATION_IN_PROGRESS_MESSAGE',
    'SOURCE_UNDER_MAINTENANCE': 'SOURCE_BRANCH_UNDER_MAINTENANCE',
    'MERGE_ALREADY_IN_PROGRESS': 'MERGE_ALREADY_IN_PROGRESS_MESSAGE',
    'AI_DATA_MODEL_MERGE_IN_PROGRESS': 'AI_DATA_MODEL_MERGE_IN_PROGRESS'
  };

  showMessageBasedOnStatusCode(response: any, type: string) {
    if (type === 'SUCCESS') {
      this.handleSuccessResponse(response);
    } else {
      this.handleErrorResponse(response);
    }
  }

  private handleSuccessResponse(response: any): void {
    this.showSuccessDialog = true;
    if (response?.targetAppId) {
      this.confirmationDialogService.close();
      this.messageFromResponse = response?.status === 201 ? response.message : this.translate.instant(this.config[response?.refType]?.successMessage) ;
      this.targetUrlForNavigation = this.utilService.getAppUrlForNavigation(
        response?.targetAppId,
        this.sourceBranchOrgId,
        this.sourceBranchVersion
      );
    } else {
      this.utilService.showMessage('success', this.translate.instant(this.config[this.refType]?.successMessage));
    }
  }

  private handleErrorResponse(response: any): void {
    const errorMessage = response?.error?.MESSAGE || '';

    if (errorMessage.includes('Limit exceeded for App Copy')) {
      this.utilService.showMessage('error', this.config[this.refType]?.concurrentLimitExceededMsg);
      return;
    }

    if (!errorMessage) {
      this.utilService.showMessage('error', this.translate.instant(this.config[this.refType]?.failureMessage));
      return;
    }

    this.handleKnownErrorMessage(errorMessage);
  }

  private handleKnownErrorMessage(errorMessage: string): void {
    const trimmedError = errorMessage.trim();
    const translationKey = this.errorCodeTranslationMap[trimmedError];
    const isProjectInitError = trimmedError === 'PROJECT_INITIALISATION_IN_PROGRESS' || trimmedError === 'PROJECT_INITIALIZATION_IN_PROGRESS';

    if (!isProjectInitError) {
      this.utilService.showMessage('error', translationKey ? this.translate.instant(translationKey) : trimmedError);
    }

    this.messageFromResponse = translationKey ? this.translate.instant(translationKey) : errorMessage;
    this.showSuccessDialog = true;
    this.targetUrlForNavigation = '';
  }

  closeSuccessDialog() {
    const appId = this.appGlobalService.get('appId');
    if (appId && appId !== 'ALL' && appId !== '') {
      this.utilService.reloadRoute();
    }
    this.showSuccessDialog = false;
    this.messageFromResponse = '';
  }

  showMessageBasedOnGenerationStatus(generationStatus: string): boolean {
    switch(generationStatus){
      case 'GENERATION_IN_PROGRESS':
      case 'GENERATION_STARTED':
        this.utilService.showMessage('error', this.translate.instant('GENERATION_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'DEPLOYMENT_STARTED':
        this.utilService.showMessage('error', this.translate.instant('DEPLOYMENT_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'MAINTENANCE_STARTED':
      case 'ROLLBACK_STARTED':
      case 'CLEANUP_STARTED':
      case 'METAINFO_READING_STARTED':
        this.utilService.showMessage('error', this.translate.instant('SOURCE_BRANCH_UNDER_MAINTENANCE'));
        return false;
      default:
        return true;
    }


  }

  showMessageBasedOnAppCopyMaintenanceStatus(appCopyMaintenanceStatus: string): boolean {
    switch(appCopyMaintenanceStatus){
      case 'APPCOPY_STARTED':
        this.utilService.showMessage('error', this.translate.instant('APP_COPY_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'UNDER_VALIDATION':
        this.utilService.showMessage('error', this.translate.instant('APP_COPY_VALIDATION_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'VALIDATION_FAILED':
        this.utilService.showMessage('error', this.translate.instant('APP_COPY_VALIDATION_FAILED_FOR_SOURCE_BRANCH'));
        return false;
      default:
        return true;
    }


  }

  showMessageBasedOnReferenceMaintenanceStatus(referenceMaintenanceStatus: string): boolean {
    switch(referenceMaintenanceStatus){
      case 'REFERENCE_VALIDATION_STARTED':
        this.utilService.showMessage('error', this.translate.instant('REFERENCE_VALIDATION_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'REFERENCE_CREATION_STARTED':
        this.utilService.showMessage('error', this.translate.instant('REFERENCE_CREATION_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      case 'REFERENCE_DELETION_STARTED':
        this.utilService.showMessage('error', this.translate.instant('REFERENCE_DELETION_IN_PROGRESS_FOR_SOURCE_BRANCH'));
        return false;
      default:
        return true;
    }
  }

}




